
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
// @ts-ignore
import { DateRange } from 'react-date-range';

import { IDashboardReportState } from '@store/slices';

import { Dropdown } from 'antd';

import { BlueButton } from '@share/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import { EXPORT_KEYS, EXPORT_TYPE, FILE_TYPE } from '@constants';
import { Urls } from '@share/constants';
import { axiosInstanceB2B, Toaster } from '@share/utils';
import { TrendsExportTypeEnum } from '@common-types';

import './style.scss';

interface IProps {
  activeTab: string;
}

export function DashboardReportFiltersButtons(props: IProps) {
  const { activeTab } = props;
  const intl = useIntl();

  const { request } = useSelector((state: { dashboardReportsStore: IDashboardReportState }) => state.dashboardReportsStore);

  const buildQuery = (endpoint: string, { dateType, fromDate, toDate }: any) => {
    let query = `/${endpoint}?dateType=${dateType}`;
    if (fromDate) query += `&fromDate=${fromDate}`;
    if (toDate) query += `&ToDate=${toDate}`;
    return query;
  };

  const exportBookings = async (params: string, fileType: EXPORT_TYPE.CSV | EXPORT_TYPE.EXCEL) => {
    try {
      const url =
        fileType === FILE_TYPE.CSV
          ? `${Urls.ExportToCSV}/${request.filterType ? request.filterType : 'all'}/exportCsv${params}`
          : `${Urls.ExportToExcel}/${request.filterType ? request.filterType : 'all'}/exportExcel${params}`;
      const response = await axiosInstanceB2B.get(url, { responseType: 'blob' });
      if (response.data) {
        const now = new Date().toISOString().split('T')[0];
        const fileExtension = fileType === FILE_TYPE.CSV ? FILE_TYPE.CSV : FILE_TYPE.EXCEL;
        const exportFileName = params.match(/\/([^/?]+)/);
        const fileName = `${
          exportFileName ? exportFileName[1] : 'bookings_export'
        }_${now}.${fileExtension}`;

        const mimeType =
          fileType === FILE_TYPE.CSV
            ? 'text/csv;charset=utf-8;'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        let blobData = response.data;
        if (fileType === FILE_TYPE.CSV) {
          const textData = await response.data.text();
          blobData = new Blob([`\uFEFF${textData}`], { type: mimeType });
        } else {
          blobData = new Blob([response.data], { type: mimeType });
        }
        const downloadUrl = window.URL.createObjectURL(blobData);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        Toaster.success(`${intl.formatMessage({ id: 'file.downloaded' })}`);
      } else {
        Toaster.info(`${intl.formatMessage({ id: 'no.data.received' })}`);
      }
    } catch (error: any) {
      console.error(`Error exporting ${fileType.toUpperCase()}:`, error);
      Toaster.error(`Failed to export ${fileType.toUpperCase()}`);
    }
  };
  
  const handleAction = async (actionType: string, fileType: string) => {
    const { dateType, fromDate, toDate } = request;

    switch (actionType) {
      case EXPORT_KEYS.TOP_DESTINATIONS: {
        const query = buildQuery(EXPORT_KEYS.TOP_DESTINATIONS, { dateType, fromDate, toDate });
        if (fileType === TrendsExportTypeEnum.CSV) {
          await exportBookings(query, EXPORT_TYPE.CSV);
        } else if (fileType === TrendsExportTypeEnum.Excel) {
          await exportBookings(query, EXPORT_TYPE.EXCEL);
        }
        break;
      }

      case EXPORT_KEYS.TOP_MEMBERS: {
        const query = buildQuery(EXPORT_KEYS.TOP_MEMBERS, { dateType, fromDate, toDate });
        if (fileType === TrendsExportTypeEnum.CSV) {
          await exportBookings(query, EXPORT_TYPE.CSV);
        } else if (fileType === TrendsExportTypeEnum.Excel) {
          await exportBookings(query, EXPORT_TYPE.EXCEL);
        }
        break;
      }

      case EXPORT_KEYS.TOTAL_SUMMARY: {
        const query = buildQuery(EXPORT_KEYS.TOTAL_SUMMARY, { dateType, fromDate, toDate });
        if (fileType === TrendsExportTypeEnum.CSV) {
          await exportBookings(query, EXPORT_TYPE.CSV);
        } else if (fileType === TrendsExportTypeEnum.Excel) {
          await exportBookings(query, EXPORT_TYPE.EXCEL);
        }
        break;
      }

      default:
        console.error('Invalid action:', actionType);
        Toaster.error('Invalid action. Please try again.');
    }
  };
  
  const isSummary = activeTab === 'summary';
  const isTopDestinatiosn = activeTab === 'top_destinations';
  
  const key = isSummary ? EXPORT_KEYS.TOTAL_SUMMARY : isTopDestinatiosn ? EXPORT_KEYS.TOP_DESTINATIONS : EXPORT_KEYS.TOP_MEMBERS;

  const exportItems = [
    {
      key: EXPORT_TYPE.CSV,
      label: 'CSV',
    },
    {
      key: EXPORT_TYPE.EXCEL,
      label: 'Excel',
    },
  ];
  
  const handleMenuClick = async (info: { key: string }) => {
    switch (key) { 
      case EXPORT_KEYS.TOP_DESTINATIONS:
      case EXPORT_KEYS.TOP_MEMBERS:
      case EXPORT_KEYS.TOTAL_SUMMARY: {
        await handleAction(key, info.key);
        break;
      }
      default:
        console.error('Invalid action key:', key);
        Toaster.error('Invalid action. Please try again.');
    }
  };
  return (
    <div className="d-flex flex-wrap mt-sm-0">
      <Dropdown 
        menu={{
          items: exportItems.map((item) => ({
            key: item.key,
            label: item.label,
          })),
          onClick: handleMenuClick, 
        }}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ borderRadius: '0 0 8px 8px' }}
      >
        <BlueButton type="primary" className="btn btn-primary" style={{ height: '38px' }}>
          <FontAwesomeIcon icon={faDownload} size="1x" style={{ marginRight: '5px' }} />
          <FormattedMessage id="export" />
        </BlueButton>
      </Dropdown>
    </div>
  );
}
